import ActionsPopOverItem from "@/components/ActionsPopOver/ActionsPopOverItem";
import FileDownloader from "@/components/FileDownloader/FileDownloader";
import ImpactPill from "@/components/Impact/ImpactPill";
import MoreButton from "@/components/MoreButton/MoreButton";
import StatusTag from "@/components/StatusTag/StatusTag";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import { IImpact } from "@/pages/dashboard/utils/types";
import UploadProductAction from "@/pages/products/actions/UploadProductAction/UploadProductAction";
import {
  GetProductScenarios,
  IProductScenario,
  useExportProductsBom,
} from "@/pages/products/useProducts";
import {
  CaretDownOutlined,
  CaretRightOutlined,
  DeleteOutlined,
  ExportOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Button, Space } from "antd";
import BillOfMaterialsTable from "../../bom/BillOfMaterialsTable";
import styles from "../ProductScenarios.module.scss";
import useOpenScenarios from "../useOpenScenarios";
import DeleteScenarioAction from "./DeleteScenarioAction";

export default function ProductScenario({
  scenario,
  productId,
  compareImpact,
}: {
  scenario: IProductScenario;
  productId: number;
  compareImpact?: IImpact;
}) {
  const { openScenarioIds, toggleScenario, openScenario } = useOpenScenarios();
  const isOpen = openScenarioIds.includes(scenario.id);
  const exportBom = useExportProductsBom();
  const smartQueryClient = useSmartQueryClient();

  return (
    <div className={`${styles.scenario} ${styles[isOpen ? "open" : "closed"]}`}>
      <div className={styles.header}>
        <span
          className={styles.name}
          onClick={() => toggleScenario(scenario.id)}
        >
          {isOpen ? <CaretDownOutlined /> : <CaretRightOutlined />}
          {scenario.name}
        </span>
        <div className={styles.buttons}>
          <Space.Compact>
            {scenario.id === 0 ? (
              <FileDownloader
                fileOutputName={`export-bom-product-${productId}.xlsx`}
                fileUrlFn={() =>
                  exportBom.mutateAsync({ productIds: [productId] }).then((data) => data.url)
                }
                icon={<ExportOutlined />}
                buttonText="Export BOM"
              />
            ) : (
              <>
                <UploadProductAction
                  productId={productId}
                  scenarioId={scenario.id}
                  onSuccess={() => {
                    smartQueryClient.setQueryData<GetProductScenarios>(
                      ["products", "detail", productId, "scenarios"],
                      (getScenarios) => {
                        if (!getScenarios) return undefined;
                        return {
                          ...getScenarios,
                          scenarios: getScenarios.scenarios.map((x) =>
                            x.id !== scenario.id ? x : { ...x, isStale: true }
                          ),
                        };
                      }
                    );
                    openScenario(scenario.id);
                  }}
                >
                  <Button icon={<UploadOutlined />}>Upload BOM</Button>
                </UploadProductAction>
                <FileDownloader
                  fileOutputName={`export-bom-product-${productId}-scenario-${scenario.id}.xlsx`}
                  fileUrlFn={() =>
                    exportBom
                      .mutateAsync({ productIds: [productId], scenarioId: scenario.id })
                      .then((data) => data.url)
                  }
                  icon={<ExportOutlined />}
                  buttonText="Export BOM"
                />
                <MoreButton
                  actions={
                    <>
                      <DeleteScenarioAction
                        productId={productId}
                        scenarioId={scenario.id}
                      >
                        <ActionsPopOverItem
                          danger
                          icon={<DeleteOutlined />}
                        >
                          Delete scenario
                        </ActionsPopOverItem>
                      </DeleteScenarioAction>
                    </>
                  }
                />
              </>
            )}
          </Space.Compact>
        </div>
        <ImpactPill
          impact={scenario.impact}
          compareImpact={compareImpact}
        />
        <StatusTag
          status={scenario.status}
          background="white"
        />
      </div>
      <div className={styles.content}>
        <BillOfMaterialsTable
          data={scenario.bom}
          noDataMessage="No bill of materials has been uploaded for this scenario yet."
        />
      </div>
    </div>
  );
}
